// pages/Contact.js — mobile responsive with PHP mail handler + honeypot + toast notification

import React, { useEffect, useState } from 'react';

export default function Contact() {
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const [form, setForm] = useState({
    name: '', email: '', phone: '', enquiry: 'Design Consultation', message: '', website: '',
  });
  const [status, setStatus] = useState('idle');
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setStatus('sending');

    try {
      const response = await fetch('https://www.urbankdcni.co.uk/mail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:    form.name,
          email:   form.email,
          phone:   form.phone || 'Not provided',
          enquiry: form.enquiry,
          message: form.message,
          website: form.website,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setStatus('success');
        setForm({ name: '', email: '', phone: '', enquiry: 'Design Consultation', message: '', website: '' });
        showToast('success', '✓ Message sent! We\'ll be in touch shortly.');
      } else {
        setStatus('error');
        showToast('error', '✗ Something went wrong. Please try again or call us.');
      }
    } catch (err) {
      console.error(err);
      setStatus('error');
      showToast('error', '✗ Something went wrong. Please try again or call us.');
    }
  };

  const contactInfo = [
    { icon: '📞', label: 'Main Office',   value: '028 9013 2146' },
    { icon: '📱', label: 'Mobile',        value: '079 1241 9641' },
    { icon: '✉',  label: 'Email',         value: 'hello@urbankdcni.co.uk' },
    { icon: '📍', label: 'Showroom',      value: '122f Upper Lisburn Road\nBelfast, BT10 0BD' },
    { icon: '🕐', label: 'Opening Hours', value: 'Tue – Fri: 10:00 AM – 2:00 PM\nMon, Sat, Sun: Closed' },
  ];

  return (
    <div className="page-wrapper--inner">
      <div style={{ height: "60px" }} />

      {/* TOAST NOTIFICATION */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: toast.type === 'success' ? '#1a1a1a' : '#1a1a1a',
          border: `1px solid ${toast.type === 'success' ? '#F47820' : '#dc3232'}`,
          color: toast.type === 'success' ? '#F47820' : '#ff6b6b',
          padding: '14px 24px',
          fontSize: '0.9rem',
          fontFamily: "'Manrope', sans-serif",
          fontWeight: 500,
          borderRadius: '2px',
          zIndex: 9999,
          whiteSpace: 'nowrap',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          animation: 'slideUp 0.3s ease',
        }}>
          {toast.message}
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(16px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        .contact-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 5rem;
          margin-bottom: 4rem;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }
        .showroom-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-top: 1rem;
        }
        @media (max-width: 768px) {
          .contact-layout {
            grid-template-columns: 1fr;
            gap: 3rem;
          }
          .form-row {
            grid-template-columns: 1fr;
          }
          .showroom-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <section className="section">
        <div className="container">
          <div className="label">Contact Us</div>
          <h2 className="heading-xl">Let's Start a<br /><span className="orange-italic">Conversation</span></h2>
          <br />
          <p className="subtext" style={{ maxWidth: 560, marginBottom: '4rem' }}>
            Ready to transform your kitchen? Get in touch to arrange a consultation
            or visit our showroom. We'd love to hear about your project.
          </p>

          <div className="contact-layout">

            {/* FORM */}
            <div>
              <h3 style={s.formTitle}>Request a Design Consultation</h3>

              {status !== 'success' && (
                <form onSubmit={handleSubmit}>

                  {/* HONEYPOT — hidden from real users */}
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                    <input
                      type="text"
                      name="website"
                      value={form.website}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div style={s.fg}>
                    <label style={s.label}>Your Name *</label>
                    <input style={s.input} type="text" name="name" value={form.name} onChange={handleChange} placeholder="John Smith" required />
                  </div>
                  <div className="form-row">
                    <div style={s.fg}>
                      <label style={s.label}>Email Address *</label>
                      <input style={s.input} type="email" name="email" value={form.email} onChange={handleChange} placeholder="john@example.com" required />
                    </div>
                    <div style={s.fg}>
                      <label style={s.label}>Phone Number</label>
                      <input style={s.input} type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="079 1234 5678" />
                    </div>
                  </div>
                  <div style={s.fg}>
                    <label style={s.label}>Enquiry Type</label>
                    <select style={s.input} name="enquiry" value={form.enquiry} onChange={handleChange}>
                      <option>Design Consultation</option>
                      <option>General Enquiry</option>
                      <option>Showroom Visit</option>
                      <option>Aftercare</option>
                    </select>
                  </div>
                  <div style={s.fg}>
                    <label style={s.label}>Your Message *</label>
                    <textarea style={{ ...s.input, minHeight: 150, resize: 'vertical' }} name="message" value={form.message} onChange={handleChange} placeholder="Tell us about your project..." required />
                  </div>

                  <button
                    type="submit"
                    style={{ ...s.sendBtn, opacity: status === 'sending' ? 0.7 : 1, cursor: status === 'sending' ? 'not-allowed' : 'pointer' }}
                    disabled={status === 'sending'}
                  >
                    {status === 'sending' ? 'Sending...' : 'Send Enquiry ✈'}
                  </button>
                </form>
              )}

              {status === 'success' && (
                <div style={s.successMsg}>✓ Thanks! We'll be in touch with you shortly.</div>
              )}
            </div>

            {/* CONTACT INFO */}
            <div>
              <h3 style={s.formTitle}>Contact Information</h3>
              {contactInfo.map((item, i) => (
                <React.Fragment key={item.label}>
                  <div style={{ ...s.ciItem, borderBottom: i < contactInfo.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={s.ciIcon}>{item.icon}</div>
                    <div>
                      <div style={s.ciLabel}>{item.label}</div>
                      <div style={{ fontSize: '0.9rem', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{item.value}</div>
                    </div>
                  </div>
                  {item.label === 'Opening Hours' && (
                    <div style={s.googleReviewWrap}>
                      <a
                        href="https://www.google.com/search?sca_esv=a75f3b595b8525f4&rlz=1C5CHFA_enGB833GB833&si=APenkKm7iecQ4G6P-TsbSMFKIQtv3EFIqRAFw-i8uEbk55Z-__TzVWADptuEtU1l6nvB6cvHLKW71lzLvFGaSNEeC0IoOzdVb_IyvLFs9jSb5eAOkLXVPJbSxDW7fH0R2O-bX89alIHkDP20PWj4l9i9i8noU-E-gg%3D%3D&q=Urban+Kitchens+KDC+Ltd+Reviews&sa=X&biw=1680&bih=872&dpr=2&zx=1784632223501&dlnr=1&ved=2ahUKEwjC2ZaO0eOVAxU3REEAHe7cJw0Ql6ENegQICRAG"
                        target="_blank"
                        rel="noreferrer"
                        style={s.googleReviewLink}
                      >
                        <span style={s.googleIcon}>G</span>
                        Read Google Reviews
                      </a>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <h3 style={{ ...s.formTitle, marginBottom: '1.5rem' }}>Visit Our Showroom</h3>
          <div className="showroom-grid">
            <img
              src="/images/urban-showroom.jpg"
              alt="Urban Kitchens showroom, 122f Upper Lisburn Road, Belfast"
              style={{
                width: '100%',
                height: 450,
                objectFit: 'cover',
                objectPosition: 'center 65%',
                display: 'block',
                border: '1px solid var(--border)',
              }}
            />
            <div style={s.mapWrap}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2314!2d-6.0035!3d54.5617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x486109dc0c86f3b9%3A0x0!2s122f+Upper+Lisburn+Rd!5e0!3m2!1sen!2suk!4v1"
                style={{ width: '100%', height: '100%', border: 'none', filter: 'grayscale(20%) saturate(70%) brightness(90%)' }}
                loading="lazy"
                title="Urban Kitchens Location"
              />
            </div>
          </div>

          <div style={s.googleReviewWrap}>
            <a
              href="https://www.google.com/search?sca_esv=a75f3b595b8525f4&rlz=1C5CHFA_enGB833GB833&si=APenkKm7iecQ4G6P-TsbSMFKIQtv3EFIqRAFw-i8uEbk55Z-__TzVWADptuEtU1l6nvB6cvHLKW71lzLvFGaSNEeC0IoOzdVb_IyvLFs9jSb5eAOkLXVPJbSxDW7fH0R2O-bX89alIHkDP20PWj4l9i9i8noU-E-gg%3D%3D&q=Urban+Kitchens+KDC+Ltd+Reviews&sa=X&biw=1680&bih=872&dpr=2&zx=1784632223501&dlnr=1&ved=2ahUKEwjC2ZaO0eOVAxU3REEAHe7cJw0Ql6ENegQICRAG"
              target="_blank"
              rel="noreferrer"
              style={s.googleReviewLink}
            >
              <span style={s.googleIcon}>G</span>
              Read Google Reviews
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

const s = {
  formTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: '1.6rem', fontWeight: 500, marginBottom: '2rem' },
  fg: { marginBottom: '1.5rem' },
  label: { display: 'block', fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.5rem' },
  input: { width: '100%', background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: "'Manrope', sans-serif", fontSize: '0.9rem', fontWeight: 300, padding: '0.85rem 1rem', outline: 'none' },
  sendBtn: { width: '100%', background: 'var(--orange)', color: '#000', fontFamily: "'Manrope', sans-serif", fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', padding: '1.1rem', border: 'none', transition: 'background 0.2s' },
  successMsg: { background: 'rgba(232,130,26,0.1)', border: '1px solid var(--orange)', color: 'var(--orange)', padding: '1.5rem', fontSize: '0.9rem', marginBottom: '1.5rem' },
  ciItem: { display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1.2rem 0' },
  ciIcon: { width: 42, height: 42, background: 'var(--bg3)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.9rem' },
  ciLabel: { fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: '0.3rem' },
  googleReviewWrap: { marginTop: '1.2rem', marginBottom: '2rem' },
  googleReviewLink: { display: 'inline-flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', borderRadius: '999px', background: 'rgba(232,130,26,0.08)', color: 'var(--text)', textDecoration: 'none', border: '1px solid rgba(232,130,26,0.2)', fontSize: '0.85rem', fontWeight: 600 },
  googleIcon: { width: 26, height: 26, borderRadius: '50%', background: '#4285F4', color: '#fff', display: 'grid', placeItems: 'center', fontSize: '0.85rem', fontWeight: 600 },
  mapWrap: { width: '100%', height: 450, border: '1px solid var(--border)', overflow: 'hidden' },
};